'use client';

import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { useAuth } from '@dashboard/context/AuthContext';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@dashboard/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@dashboard/components/ui/collapsible';

import { getFilteredNavGroups, isPathInGroup, type AdminNavGroup } from '../config/adminNavigation';

export function AdminSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth();
  const location = useLocation();

  // Filter navigation groups based on user role
  const navGroups = React.useMemo(() => getFilteredNavGroups(user?.role || ''), [user?.role]);

  // Flat list of all nav items for checking overlapping routes
  const allNavItems = React.useMemo(() => navGroups.flatMap(group => group.items), [navGroups]);

  // Determine which groups should be open by default
  // Open the group that contains the current route, or groups with defaultOpen
  const getDefaultOpenState = React.useCallback(
    (group: AdminNavGroup): boolean => {
      if (isPathInGroup(location.pathname, group)) {
        return true;
      }
      return group.defaultOpen ?? false;
    },
    [location.pathname],
  );

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader className="border-b border-gray-200 dark:border-border px-3 py-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Back to dashboard"
              size="lg"
              className="hover:bg-gray-100 dark:hover:bg-muted"
            >
              <Link to="/" className="flex items-center gap-2">
                <ChevronLeftIcon className="h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                <span className="font-medium text-gray-900 dark:text-foreground">Dashboard</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="px-2 py-2">
        {navGroups.map(group => (
          <Collapsible
            key={group.title}
            defaultOpen={getDefaultOpenState(group)}
            className="group/collapsible"
          >
            <SidebarGroup className="p-0 py-2">
              <SidebarGroupLabel
                asChild
                className="h-9 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-muted-foreground hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-700 dark:hover:text-muted-foreground rounded-md cursor-pointer transition-colors"
              >
                <CollapsibleTrigger className="flex w-full items-center gap-2">
                  <group.icon className="h-4 w-4" />
                  <span className="flex-1 text-left">{group.title}</span>
                  <ChevronRightIcon className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent className="mt-1">
                  <SidebarMenu className="ml-4 border-l border-gray-200 dark:border-border pl-2">
                    {group.items.map(item => {
                      // Check both with and without /dashboard prefix for React Router compatibility
                      // Use exact match, or startsWith only if no other nav item is a more specific match
                      const pathMatchesPrefix =
                        location.pathname.startsWith(`${item.href}/`) ||
                        location.pathname.startsWith(`/dashboard${item.href}/`);
                      const hasMoreSpecificMatch =
                        pathMatchesPrefix &&
                        allNavItems.some(
                          other =>
                            other.href !== item.href &&
                            other.href.startsWith(`${item.href}/`) &&
                            (location.pathname === other.href ||
                              location.pathname.startsWith(`${other.href}/`) ||
                              location.pathname === `/dashboard${other.href}` ||
                              location.pathname.startsWith(`/dashboard${other.href}/`)),
                        );
                      const isActive =
                        location.pathname === item.href ||
                        location.pathname === `/dashboard${item.href}` ||
                        (pathMatchesPrefix && !hasMoreSpecificMatch);
                      return (
                        <SidebarMenuItem key={item.href}>
                          <Link to={item.href} className="block">
                            <SidebarMenuButton
                              isActive={isActive}
                              tooltip={item.name}
                              size="sm"
                              className={isActive ? '!bg-muted !text-foreground !font-medium' : ''}
                            >
                              <span className="truncate">{item.name}</span>
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        ))}
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
}

export default AdminSidebar;
